// Copyright (c) 2022 by Move Closer

import { AnyObject } from '@movecloser/front-core'

import { ImageData, IRelatedService, ModuleContentResolver } from '../../contexts'
import { logger } from '../../support'

import { ResolvedLink } from '../partials'
import { resolveImage, resolveLink } from '../resolvers'

import { resolveCreationsModuleContent } from '../Creations/Creations.helpers'
import { resolveImageModuleContent } from '../Image/Image.helpers'

import {
  AddonData,
  HeroModuleContent,
  HeroModuleContentInput,
  PossibleAddons,
  Slide
} from './Hero.contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
const resolversMap = {
  [PossibleAddons.Creations]: resolveCreationsModuleContent,
  [PossibleAddons.Image]: resolveImageModuleContent
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const resolveHeroModuleContent: ModuleContentResolver<HeroModuleContentInput, HeroModuleContent> =
  async (input: HeroModuleContentInput, relatedService: IRelatedService): Promise<HeroModuleContent> => {
    let slides: Slide[] = []
    try {
      slides = await Promise.all(input.slides.map(
        async (
          {
            addon,
            background,
            video,
            link,
            additionalLink,
            ...rest
          }
        ) => {
          let resolvedAddon: AnyObject | undefined
          let resolvedImage: ImageData | null = null
          let resolvedVideo: ImageData | null = null
          let resolvedLink: ResolvedLink | undefined
          let resolvedAdditionalLink: ResolvedLink | undefined

          if (addon && typeof resolversMap[addon.type] === 'function') {
            try {
              resolvedAddon = {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                content: await resolversMap[addon.type](addon.content, relatedService),
                type: addon.type
              }
            } catch (e) {
              logger(e, 'warn')
            }
          }

          if (background) {
            try {
              resolvedImage = await resolveImage(background, relatedService)
            } catch (e) {
              resolvedImage = null
            }
          }

          if (video) {
            try {
              resolvedVideo = await resolveImage(video, relatedService)
            } catch (e) {
              resolvedVideo = null
            }
          }

          if (link) {
            if (link.relatedTarget) {
              resolvedLink = await resolveLink(link, relatedService)
            } else {
              resolvedLink = link as ResolvedLink
            }
          }

          if (additionalLink) {
            if (additionalLink.relatedTarget) {
              resolvedAdditionalLink = await resolveLink(additionalLink, relatedService)
            } else {
              resolvedAdditionalLink = additionalLink as ResolvedLink
            }
          }

          return {
            background: resolvedImage,
            video: resolvedVideo,
            link: resolvedLink,
            additionalLink: resolvedAdditionalLink,
            addon: resolvedAddon as AddonData,
            ...rest
          }
        }))
    } catch (e) {
      logger(e, 'warn')
    }

    return {
      ...input,
      slides
    }
  }
